'use client'
import { Button } from '@/components/ui/button/button'
import { FormMetadata, Prerequisite } from '@/lib/fetching/schema'
import { BookText, ChevronRightIcon, Info } from 'lucide-react'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { Alert, AlertTitle } from '@/components/ui/alert/alert'

interface PrerequisitesComponentProps {
  prerequisites_title: string
  prerequisites: Prerequisite[]
}

const ImageIcon: React.FC<{ src?: string; alt: string }> = ({ src, alt }) => {
  return src ? (
    <Image
      src={`data:image/svg+xml;base64,${src}`}
      alt={alt}
      width={24}
      height={24}
    />
  ) : (
    <BookText name="image" width={30} height={30} />
  )
}

interface NotesSectionProps {
  notes: string[]
}

const NotesSection: React.FC<NotesSectionProps> = ({ notes }) => {
  if (!notes || notes.length === 0) return null

  return (
    <div className="rounded-2xl bg-[#F3F6FD] p-6">
      <h2 className="mb-6 text-[18px] font-semibold text-gray-900">
        Please Note:
      </h2>

      <ul className="list-disc space-y-4 pl-5 text-[14px] font-normal text-[#111827]">
        {notes.map((note, index) => (
          <li key={`${note}-${index}`}>{note}</li>
        ))}
      </ul>
    </div>
  )
}

interface NoteAlertProps {
  note?: string
}

const NoteAlert: React.FC<NoteAlertProps> = ({ note }) => {
  if (!note) return null

  return (
    <Alert className="mt-5 bg-[#F4F0FF] px-8 py-3">
      <div className="flex items-center gap-3">
        <Info className="size-5 shrink-0 stroke-black" />

        <AlertTitle className="font-inter mt-[4px] text-[14px] font-medium leading-[20px] tracking-normal text-[#111827]">
          Note : {note}
        </AlertTitle>
      </div>
    </Alert>
  )
}

const PrerequisitesComponent: React.FC<PrerequisitesComponentProps> = ({
  prerequisites,
  prerequisites_title,
}) => {
  if (!prerequisites || prerequisites.length === 0) return null

  return (
    <div className="overflow-y-auto pt-8">
      <div className="rounded-2xl border border-gray-200 bg-white pb-2 pt-5 shadow-sm">
        <h2 className="mb-6 ml-5 text-[18px] font-semibold text-[#111827]">
          {prerequisites_title}
        </h2>

        <div className="flex flex-col gap-4">
          {prerequisites.map((prerequisite, index) => (
            <div
              key={`${prerequisite.description}-${index}`}
              className={`flex flex-col gap-2 py-0 transition-all ${
                prerequisite.highlight
                  ? 'mx-2 rounded-xl bg-[#F4F0FF] py-3 pl-[12px]'
                  : 'mx-5 bg-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`shrink-0 ${
                    prerequisite.highlight
                      ? 'font-semibold text-gray-900'
                      : 'font-normal text-[#111827]'
                  }`}
                >
                  <ImageIcon src={prerequisite.icon?.data} alt="icon" />
                </div>

                <p
                  className={`text-[14px] leading-[20px] ${
                    prerequisite.highlight
                      ? 'font-semibold text-gray-900'
                      : 'font-normal text-[#111827]'
                  }`}
                >
                  {prerequisite.description}
                </p>
              </div>

              {prerequisite.subPoints && prerequisite.subPoints.length > 0 && (
                <ul className="-mt-2 ml-10 list-disc px-6 pb-3 text-[14px] text-[#111827]">
                  {prerequisite.subPoints.map((subPoint, index) => (
                    <li key={`${subPoint}-${index}`}>{subPoint}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export interface PreRequisiteProps {
  formSchema: FormMetadata | undefined
  formId: string
  source_type?: string
  Lg?: string
  Lc?: string
}

export default function PrerequisiteContent({
  formSchema,
  formId,
  source_type,
  Lg,
  Lc,
}: Readonly<PreRequisiteProps>) {
  const router = useRouter()

  const alert = formSchema?.alert ?? []
  const notes = formSchema?.notes ?? []

  const handleProceed = () => {
    const params = new URLSearchParams()
    params.append('redirect', `/forms/${formId}`)

    if (Lg) params.append('Lg', Lg)
    if (Lc) params.append('Lc', Lc)
    if (source_type) params.append('source_type', source_type)

    if (formSchema?.uniqueJourney) {
      const query = params.toString()
      let url = `/login/forms/${formId}`

      if (query) {
        url += `?${query}`
      }
      router.push(url)
      return
    }
    const query = params.toString()
    router.push(`/login?${query}`)
  }
  return (
    <div className="size-full overflow-y-auto p-10">
      <div>
        <h1 className="text-[28px] font-semibold text-[#111827]">
          {formSchema?.name ?? ''}
        </h1>
        <p className="text-sm font-normal text-[#4B5563]">
          {formSchema?.description ?? ''}
        </p>
      </div>

      <PrerequisitesComponent
        prerequisites={formSchema?.prerequisites ?? []}
        prerequisites_title={formSchema?.prerequisiteTitle ?? ''}
      />

      <div className="mt-5 space-y-5">
        {alert.length > 0 && <NoteAlert note={alert[0]} />}
        {notes.length > 0 && <NotesSection notes={notes} />}
      </div>

      <div>
        <Button
          className="mb-1 mt-8 flex h-[52px] w-full max-w-[349px] items-center justify-center gap-1 rounded-[13px] bg-[#1C3FCA] px-5 py-[14px] font-semibold"
          variant="default"
          onClick={handleProceed}
          type="button"
          transactionName="prerequisite.letsbegin.click"
        >
          Let&apos;s Begin <ChevronRightIcon />
        </Button>
      </div>
    </div>
  )
}
